import React, { useMemo } from 'react'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'components/Luxkit/Typography/Caption'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'

import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceWithCaption from 'components/Luxkit/PricePoints/PriceRowPriceWithCaption'
import LuxPlusPriceGuard from 'loyalty/components/LuxPlusPriceGuard'
import PriceRowLoyaltyPriceWithCaption from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPriceWithCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import { isActiveLuxPlusMember } from 'loyalty/selectors/featureToggle'

interface Props {
  selectedDay: App.CalendarDay;
  offer: App.Offer;
  isAnyFlightsActive?: boolean;
  duration: number;
  isFromPrice?: boolean;
  displayMemberPrice: boolean;
  isActiveLuxPlusMember: boolean;
}

function OfferCalenderDatePickerFooterPriceDetails(props: Props) {
  const { selectedDay, offer, isAnyFlightsActive, duration, isFromPrice, displayMemberPrice, isActiveLuxPlusMember } = props
  const canShowPrice = !(offer.bundledWithFlightsOnly && !selectedDay?.flightCacheActive)

  const [price, value, discount] = useMemo(():[number, number, number] => {
    const selectedDayValue = isActiveLuxPlusMember ? selectedDay?.hotelMemberValue : selectedDay?.hotelValue

    if (selectedDay) {
      const isDiscountPillHidden = offer.isDiscountPillHidden
      const discount = isDiscountPillHidden ? 0 : Math.round((selectedDayValue - selectedDay.hotelPrice) * 100 / selectedDayValue)
      if (isAnyFlightsActive) {
        return [selectedDay.packagedTotal, selectedDayValue + selectedDay.flightPrice, discount]
      }
      return [selectedDay.hotelTotal, selectedDayValue, discount]
    }
    return [0, 0, 0]
  }, [isActiveLuxPlusMember, selectedDay, offer.isDiscountPillHidden, isAnyFlightsActive])

  const shouldShowDiscountPercent = (discount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE) && !offer.isDiscountPillHidden
  const durationCaption = pluralizeToString('night', duration)

  const {
    saleUnit,
    total: displayPrice,
    value: displayValue,
  } = useOfferPrice({
    value,
    saleUnit: 'total',
    duration,
    total: price,
  })

  if (!canShowPrice) {
    return <Caption variant="large">
      {durationCaption}
    </Caption>
  }

  return <div>
    <LuxPlusPriceGuard
      showMemberPrice={displayMemberPrice}
      fallbackPrice={
        <PriceRowPriceWithCaption
          size="M"
          caption={`${durationCaption} ${isFromPrice ? 'from' : ''}`}
          price={displayPrice}
          saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        />}
    >
      <PriceRowLoyaltyPriceWithCaption
        caption={`${durationCaption} ${isFromPrice ? 'from' : ''}`}
        memberPrice={displayPrice}
        saleUnit={`${saleUnit}${isAnyFlightsActive ? '*' : ''}`}
        size="M"
      />
    </LuxPlusPriceGuard>
    <PriceRowTaxesAndFees />
    {value > price && offer.shouldDisplayValue && displayValue && <PriceRowValueDiscountWithCaption
      size="M"
      value={displayValue}
      discountPercentage={!isAnyFlightsActive && shouldShowDiscountPercent ? discount : undefined}
    />}
  </div>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    displayMemberPrice: state.displayMemberPrice,
    isActiveLuxPlusMember: isActiveLuxPlusMember(appState),
  }
})(OfferCalenderDatePickerFooterPriceDetails)
